import { Plus } from 'lucide-react';

const dayName = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' });
const TONE = {
  covered: 'bg-[#EEF4F2] border-[#C9DAD2] text-[#2F5D52]',
  gap: 'bg-[#FBEDED] border-[#E8C9C9] text-[#8A3A3A]',
  cancelled: 'bg-slate-50 border-slate-200 text-slate-400 line-through',
};
const TYPE_TAG = { direct: 'BT', supervision: 'SUP', parent_training: 'PT', assessment: 'ASM' };

export const Block = ({ b, staffName, onClick }) => (
  <button onClick={() => onClick(b)} data-testid={`sched-block-${b.session_id}-${b.date}`}
    className={`w-full text-left rounded-lg border px-2 py-1 text-[11px] leading-tight transition-transform hover:-translate-y-0.5 ${TONE[b.status]} ${b.reassigned ? 'ring-1 ring-[#B7A6CC]' : ''}`}>
    <div className="font-semibold">{b.start}–{b.end} <span className="opacity-60">{TYPE_TAG[b.type]}</span></div>
    <div className="truncate">{b.staff_id ? staffName(b.staff_id) : 'Unassigned'}{b.reassigned ? ' (cover)' : ''}</div>
    {b.issues?.length > 0 && <div className="truncate opacity-80">{b.issues[0]}</div>}
  </button>
);

// Rows = clients, columns = days. Shows weekly scheduled vs authorized hours per client.
export const WeekBoard = ({ wk, days, staffName, onBlock, onAdd }) => {
  const dates = wk.dates.filter((d, i) => days.includes(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'][i]));
  return (
    <div className="overflow-auto rounded-2xl border border-[#E5E0EA] bg-white" data-testid="sched-week-board">
      <table className="min-w-full text-sm">
        <thead><tr className="bg-[#F7F5FA] text-[#6E5F78] text-[12px]">
          <th className="sticky left-0 bg-[#F7F5FA] text-left px-3 py-2 min-w-[190px]">Client · hrs/wk</th>
          {dates.map((d) => <th key={d} className="px-2 py-2 text-left min-w-[150px]">{dayName(d)}</th>)}
        </tr></thead>
        <tbody>
          {wk.clients.map((c) => (
            <tr key={c.client_id} className="border-t border-[#EEEAF2] align-top" data-testid={`sched-client-row-${c.client_id}`}>
              <td className="sticky left-0 bg-white px-3 py-2">
                <div className="font-medium text-[#2D2833]">{c.client_name}</div>
                <div className={`text-[11px] ${c.authorized_direct && c.scheduled_direct < c.authorized_direct ? 'text-[#8A3A3A]' : 'text-[#8A7B95]'}`}>
                  Direct {c.scheduled_direct}{c.authorized_direct ? ` / ${c.authorized_direct} auth` : ''} · Sup {c.scheduled_supervision}/{c.required_supervision}
                </div>
              </td>
              {dates.map((d, i) => {
                const mine = wk.blocks.filter((b) => b.client_id === c.client_id && b.date === d).sort((a, b) => a.start.localeCompare(b.start));
                return (
                  <td key={d} className="px-1.5 py-1.5 space-y-1 group">
                    {mine.map((b) => <Block key={b.session_id} b={b} staffName={staffName} onClick={onBlock} />)}
                    <button onClick={() => onAdd(c.client_id, ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'][wk.dates.indexOf(d)])}
                      className="w-full opacity-0 group-hover:opacity-100 text-[11px] text-[#8A7B95] border border-dashed border-[#E5E0EA] rounded-lg py-0.5 flex items-center justify-center gap-1"
                      data-testid={`sched-add-${c.client_id}-${i}`}><Plus size={11} /> add</button>
                  </td>
                );
              })}
            </tr>
          ))}
          {wk.clients.length === 0 && <tr><td colSpan={dates.length + 1} className="p-8 text-center text-[#8A7B95]" data-testid="sched-board-empty">No clients to schedule yet. Clients appear here once they reach Authorization or Start of Care, or once you add a session.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

export default WeekBoard;
